import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { debounceTime, take } from 'rxjs/operators';
import { fromEvent, merge } from 'rxjs';

import { AuthService } from '@nrwl/rx-service-auth';
import { DialogService } from '@nrwl/dialog-service';
import { HttpClient } from '@angular/common/http';
import { RxDialogComponent } from '@nrwl/rx-dialog';
import { RxIdleUserComponent } from './rx-idle-user.component';
import { environment } from '@nrwl/environment';

@Injectable({
  providedIn: 'root',
})
export class RxIdleUserWatchService implements OnDestroy {
  public DEBOUNCE_TIME = 100;
  public LOGOUT_TIME = 13 * 60 * 1000; //13 minutes
  public CHECK_INTERVAL = 1000; // 1 second
  public PING_TIME = 5 * 60 * 1000; // 5 minutes
  public lastAction = Date.now();
  public lastPing = Date.now();
  public dialogRef = null;
  public isDialogOpen = false;
  private storageListener: any;

  constructor(
    private _ngZone: NgZone,
    private _dialogService: DialogService,
    private httpClient: HttpClient,
    private authService: AuthService,
  ) {}

  public ngOnDestroy() {
    // Clean up listeners
    if (this.storageListener) {
      window.removeEventListener('storage', this.storageListener);
    }
  }

  public init() {
    this.initListener();
    this.initStorageListener();
    this.initInterval();
  }

  public initListener() {
    this._ngZone.runOutsideAngular(() => {
      const touchEndSource = fromEvent(document, 'touchend');
      const clickSource = fromEvent(document, 'click');
      const keyboardSource = fromEvent(document, 'keyup');

      merge(touchEndSource, clickSource, keyboardSource)
        .pipe(debounceTime(this.DEBOUNCE_TIME))
        .subscribe((response) => {
          this.resetTimeout();
          localStorage.setItem(environment.localStorage.lastUserActivity, Date.now().toString());
        });
    });
  }

  public initInterval() {
    this._ngZone.runOutsideAngular(() => {
      setInterval(() => {
        this.check();
      }, this.CHECK_INTERVAL);
    });
  }

  public resetTimeout() {
    this.lastAction = Date.now();

    if (this.dialogRef) {
      this.dialogRef.close();
      this.isDialogOpen = false;
    }
  }

  public check() {
    const now = Date.now();
    const timeleftToTimeout = this.lastAction + this.LOGOUT_TIME;
    const diffTimeout = timeleftToTimeout - now;
    const isTimeout = diffTimeout < 0;

    const timeLeftToPing = this.lastPing + this.PING_TIME;
    const diffPing = timeLeftToPing - now;
    const canPing = diffPing < 0;

    this._ngZone.run(() => {
      if (isTimeout && !this.isDialogOpen) {
        this.isDialogOpen = true;
        this.openDialog();
        this.lastAction = Date.now();
      }

      if (canPing && !isTimeout) {
        this.lastPing = Date.now();
        return this.httpClient
          .get(`${environment.baseAPIUrlv3}ping/`)
          .pipe(take(1))
          .subscribe((data) => {});
      }
      return null;
    });
  }

  public openDialog() {
    const config = {
      payload: {
        title: 'Your session is about to expire',
        tipClose: true,
      },
    };
    (config as any).component = RxIdleUserComponent;
    this.dialogRef = this._dialogService.open(RxDialogComponent, config);
  }

  public initStorageListener() {
    this.storageListener = (event: StorageEvent) => {
      if (event.key === environment.localStorage.lastUserActivity) {
        this._ngZone.run(() => {
          if (!localStorage.getItem(environment.localStorage.token)) {
            this.authService.logout();
            return;
          }
          // Activity detected in another tab - reset our timer
          this.resetTimeout();

          // Update lastAction to prevent immediate timeout
          this.lastAction = Date.now();

          // Close dialog if open since user is active in another tab
          if (this.dialogRef) {
            this.dialogRef.close();
            this.isDialogOpen = false;
          }
        });
      }
    };

    window.addEventListener('storage', this.storageListener);
  }
}
